import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import LoadingSpinner from '../../../LoadingSpinner/LoadingSpinner';
import SystemMessage from '../../../SystemMessage/SystemMessage';
import WelcomeMessageVariation from './WelcomeMessageVariation';
import {
  getNames as buildNames,
  getSavedWelcomeMessage,
  fetchWelcomeMessageVariations,
  fetchRephrasedWelcomeMessage,
  saveWelcomeMessageToCookie,
} from './PersonalizationWelcomeMessageHelpers';
import { personalizationDataShape } from '../formPropTypes';

const PersonalizeWelcomeMessage = ({
  cookieDataDump,
  isSubmitting,
  handleGoBack,
  onSaveWelcomeMessage,
  residentUuid,
}) => {
  const [welcomeMessage, setWelcomeMessage] = useState('');
  const [variations, setVariations] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [isFirstRewrite, setIsFirstRewrite] = useState(true);
  const [isRephrasingIndex, setIsRephrasingIndex] = useState(null);

  const topRef = useRef(null);
  const textareaRef = useRef(null);
  const variationCounter = useRef(0);

  const nextVariationNumber = () => {
    variationCounter.current += 1;
    return variationCounter.current;
  };

  const getNames = (conjunction = 'and') => buildNames(cookieDataDump, conjunction);

  useEffect(() => {
    const savedMessage = getSavedWelcomeMessage();
    if (savedMessage) {
      setWelcomeMessage(savedMessage);
    } else {
      setWelcomeMessage(
        `Hello ${getNames()}, I really enjoyed our conversation today! I hope the tour was enjoyable and that you're excited about everything our community has to offer. This is your Personalized Interactive Brochure, which includes all the floor plans and amenities you liked. Feel free to pass the link along to your family and friends. I'll touch base with you in a few days.`,
      );
    }
  }, [cookieDataDump.first_name, cookieDataDump.person2_first_name]);

  const adjustTextareaHeight = () => {
    if (textareaRef.current) {
      textareaRef.current.style.height = 'auto';
      textareaRef.current.style.height = `${textareaRef.current.scrollHeight}px`;
    }
  };

  useEffect(() => {
    adjustTextareaHeight();
  }, [welcomeMessage]);

  const handleCloseClick = () => {
    handleGoBack();
  };

  const handleRewriteWithAI = async () => {
    setIsLoading(true);
    setError(null);
    try {
      const data = await fetchWelcomeMessageVariations(welcomeMessage);

      const newVariations = data.map((item) => ({
        text: item.message.replace('[NAME]', getNames()),
        canRephrase: true,
        rephrased: false,
        number: nextVariationNumber(),
      }));
      setVariations((prevVariations) => [...newVariations, ...prevVariations]);
      setIsFirstRewrite(false);
    } catch (err) {
      setError(err.isEndpointReason ? err.message : 'Error fetching welcome messages. Please try again.');
      console.error('Error fetching welcome messages:', err);
    } finally {
      setIsLoading(false);
    }
  };

  const handleSaveWelcomeMessage = async () => {
    try {
      await saveWelcomeMessageToCookie(welcomeMessage, residentUuid);
      onSaveWelcomeMessage(welcomeMessage);
    } catch (err) {
      console.error('Error saving welcome message:', err);
      setError('Failed to save welcome message. Please try again.');
    }
  };

  const scrollToTop = () => {
    textareaRef.current?.focus({ preventScroll: true });
    topRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const handleSelectVariation = async (e, index) => {
    e.preventDefault();
    const selectedMessage = variations[index].text;
    setWelcomeMessage(selectedMessage);
    try {
      await saveWelcomeMessageToCookie(selectedMessage, residentUuid);
      onSaveWelcomeMessage(selectedMessage);
    } catch (error) {
      console.error('Error saving selected variation:', error);
      setError('Failed to save selected variation. Please try again.');
    }
  };

  const handleRephraseWithAI = async (index) => {
    setError(null);
    setIsRephrasingIndex(index);
    setVariations((prevVariations) => prevVariations.map(
      (v, i) => (i === index ? { ...v, canRephrase: false } : v),
    ));

    try {
      const messageContent = await fetchRephrasedWelcomeMessage(variations[index].text);
      const newMessage = messageContent.replace('[NAME]', getNames());

      setVariations((prevVariations) => {
        const next = [...prevVariations];
        next.splice(index, 0, {
          text: newMessage,
          canRephrase: false,
          rephrased: true,
          number: prevVariations[index].number,
        });
        return next;
      });
    } catch (err) {
      setError(err.isEndpointReason ? err.message : 'Error updating welcome message. Please try again.');
      console.error('Error updating welcome message:', err);
    } finally {
      setIsRephrasingIndex(null);
    }
  };

  return (
    <div
      ref={topRef}
      tabIndex="-1"
      data-testid="personalization-step:welcome-message"
    >
      <div className="modal__section">
        <div className="modal__title modal__title--long modal__subsection">
          Welcome Message for
          {' '}
          {getNames('&')}
        </div>
        <div className="modal__subsection modal__description modal__description--long">
          Create a welcome message for
          {' '}
          {getNames('&')}
          's YourTour Brochure.
          {' '}
          Use the message below, edit it, or enter your own.
          {' '}
          For a little help from AI, click CREATE AI VARIATIONS.
          {' '}
          When you find the version you like best, click it's SELECT & SAVE MESSAGE.
        </div>
      </div>

      <div className="modal__section">
        <textarea
          ref={textareaRef}
          value={welcomeMessage}
          onChange={(e) => setWelcomeMessage(e.target.value)}
          style={{ overflow: 'hidden' }}
          className="modal__group"
          // eslint-disable-next-line jsx-a11y/no-autofocus
          autoFocus
          data-testid="welcome-message-textarea"
        />

        <div className="modal__group modal__btns modal__btns--inline">
          <button
            type="button"
            className=" modal__btn modal__btn--small"
            onClick={handleRewriteWithAI}
            disabled={isSubmitting || isLoading}
            data-testid="welcome-message-generate"
          >
            {isLoading ? 'Working...' : 'Create AI Variations'}
          </button>
        </div>
      </div>

      <div className="modal__section modal__btns modal__btns--inline">

        <button
          type="button"
          className="modal__btn modal__btn--secondary modal__btn--inline"
          onClick={() => handleSaveWelcomeMessage().catch(console.error)}
          disabled={isSubmitting}
          data-testid="welcome-message-save"
        >
          Select & Save Message
        </button>
        <button
          type="button"
          className="modal__textBtn"
          onClick={handleCloseClick}
          disabled={isSubmitting}
        >
          Or Cancel Message
        </button>

      </div>

      { ((error || isFirstRewrite) && !isLoading) && (
        <div className="modal__section">
          <SystemMessage status="error" message={error} testId="welcome-message-error" />

          <SystemMessage
            status="info"
            message={isFirstRewrite ? 'Your 3 AI variations will be shown here.' : null}
            showIcon={false}
          />
        </div>
      )}

      <div className="modal__section">
        {isLoading && (
          <div className="personalization__loading">
            <LoadingSpinner />
          </div>
        )}

        <div>
          {variations.length > 0 && (
            variations.map((variation, index) => (
              <WelcomeMessageVariation
                key={index}
                variation={variation}
                index={index}
                isRephrasingIndex={isRephrasingIndex}
                onRephrase={handleRephraseWithAI}
                onEditManually={(text) => {
                  setWelcomeMessage(text);
                  scrollToTop();
                }}
                onSelect={handleSelectVariation}
              />
            ))
          )}
        </div>

      </div>

    </div>
  );
};

PersonalizeWelcomeMessage.propTypes = {
  cookieDataDump: personalizationDataShape.isRequired,
  isSubmitting: PropTypes.bool.isRequired,
  handleGoBack: PropTypes.func.isRequired,
  onSaveWelcomeMessage: PropTypes.func.isRequired,
  residentUuid: PropTypes.string.isRequired,
};

export default PersonalizeWelcomeMessage;
